// Gulati (female) / Tanaka (male) formulas, clamped to 120..200 BPM
export function calculateMHR({ age = 30, gender = "male" } = {}) {
  let mhr;
  if ((gender || "").toLowerCase() === "female") {
    mhr = Math.round(206 - 0.88 * age);
  } else {
    mhr = Math.round(208 - 0.7 * age);
  }
  return Math.max(120, Math.min(200, mhr));
}

// Zone boundaries as % of MHR (matches iOS exactly)
export function getZoneThresholds(mhr) {
  return {
    0: [Math.round(mhr * 0.4), Math.round(mhr * 0.5)],
    1: [Math.round(mhr * 0.5), Math.round(mhr * 0.6)],
    2: [Math.round(mhr * 0.6), Math.round(mhr * 0.7)],
    3: [Math.round(mhr * 0.7), Math.round(mhr * 0.8)],
    4: [Math.round(mhr * 0.8), Math.round(mhr * 0.9)],
    5: [Math.round(mhr * 0.9), mhr],
  };
}

// Solid colors from iOS HeartRateZonePalette (improved contrast so Z0 and Z1 are distinct and visible)
export const ZONE_CHART_COLORS = {
  5: "#FF5E5E",
  4: "#FFB800",
  3: "#22C55E",
  2: "#3B82F6",
  1: "#94A3B8",
  0: "#CBD5E1",
};

/** Match iOS drawCustomHrCurve zone band alpha (~0.14) so the glowing curve pops. */
export const ZONE_BAND_OPACITY = 0.14;

/** Apple-style HR curve stroke used on iOS performance charts. */
export const HR_CURVE_STROKE = "#FF334B";
export const HR_GLOW_COLOR = "#FF3B30";

/** Theme-aware zone band styling with distinguished colors, opacities, and divider borders. */
export function getZoneBandFill(zone, isDark = false) {
  if (isDark) {
    switch (zone) {
      case 5:
        return { color: "#FF453A", opacity: 0.16, border: "rgba(255, 69, 58, 0.28)" };
      case 4:
        return { color: "#FF9F0A", opacity: 0.14, border: "rgba(255, 159, 10, 0.24)" };
      case 3:
        return { color: "#30D158", opacity: 0.13, border: "rgba(48, 209, 88, 0.22)" };
      case 2:
        return { color: "#0A84FF", opacity: 0.14, border: "rgba(10, 132, 255, 0.24)" };
      case 1:
        return { color: "#64748B", opacity: 0.14, border: "rgba(148, 163, 184, 0.20)" };
      case 0:
        return { color: "#334155", opacity: 0.15, border: "rgba(100, 116, 139, 0.20)" };
      default:
        return { color: "#334155", opacity: 0.12, border: "rgba(255, 255, 255, 0.08)" };
    }
  } else {
    switch (zone) {
      case 5:
        return { color: "#FF4D4D", opacity: 0.14, border: "rgba(255, 77, 77, 0.24)" };
      case 4:
        return { color: "#FFA927", opacity: 0.14, border: "rgba(255, 169, 39, 0.24)" };
      case 3:
        return { color: "#22C55E", opacity: 0.12, border: "rgba(34, 197, 94, 0.22)" };
      case 2:
        return { color: "#3B82F6", opacity: 0.12, border: "rgba(59, 130, 246, 0.22)" };
      case 1:
        return { color: "#94A3B8", opacity: 0.12, border: "rgba(148, 163, 184, 0.20)" };
      case 0:
        return { color: "#CBD5E1", opacity: 0.14, border: "rgba(203, 213, 225, 0.24)" };
      default:
        return { color: "#CBD5E1", opacity: 0.10, border: "rgba(0, 0, 0, 0.06)" };
    }
  }
}

export function shortZoneLabel(zone) {
  switch (zone) {
    case 5:
      return "Z5 Peak";
    case 4:
      return "Z4 Hard";
    case 3:
      return "Z3 Aerobic";
    case 2:
      return "Z2 Fat Burn";
    case 1:
      return "Z1 Light";
    default:
      return "Z0 Warmup";
  }
}

// Legend + chart palette — solids match iOS; bandColor kept for soft live-trend fills
export const ZONE_DEFS = [
  { zone: 5, name: "Maximum Effort (Anaerobic)", color: ZONE_CHART_COLORS[5], bandColor: "#FFE6E6" },
  { zone: 4, name: "Hard Effort (Threshold)", color: ZONE_CHART_COLORS[4], bandColor: "#FFF8E1" },
  { zone: 3, name: "Moderate Effort (Aerobic)", color: ZONE_CHART_COLORS[3], bandColor: "#F0F8F0" },
  { zone: 2, name: "Light Effort (Fat Burning)", color: ZONE_CHART_COLORS[2], bandColor: "#F0F8FF" },
  { zone: 1, name: "Very Light Effort", color: ZONE_CHART_COLORS[1], bandColor: "#F1F5F9" },
  { zone: 0, name: "Inactive Zone", color: ZONE_CHART_COLORS[0], bandColor: "#F8FAFC" },
];

/** Zone plot bands for HR charts (40%–100% MHR), iOS-aligned with theme support. */
export function getZoneChartBands(mhr, isDark = false) {
  const thresholds = getZoneThresholds(mhr);
  return ZONE_DEFS.map(({ zone, name, color }) => {
    const [lo, hi] = thresholds[zone];
    const fill = getZoneBandFill(zone, isDark);
    return {
      zone,
      name,
      color: fill.color,
      fillOpacity: fill.opacity,
      borderColor: fill.border,
      legendColor: color,
      y1: lo,
      y2: hi,
    };
  });
}

/** Fixed Y domain used by iOS HR performance curve (40%–100% MHR). */
export function getHrChartYDomain(mhr) {
  const max = Math.max(120, Math.round(mhr) || 180);
  return [Math.round(max * 0.4), max];
}

// MET values per zone — from Swift zoneMETs
const ZONE_METS = { 0: 1.0, 1: 2.0, 2: 3.5, 3: 5.0, 4: 7.0, 5: 10.0 };

export function calculateZoneDurations({ mhr, hrPoints, totalDurationSec }) {
  const thresholds = getZoneThresholds(mhr);
  const durations = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  if (!hrPoints?.length || totalDurationSec <= 0) return durations;
  const perSample = totalDurationSec / hrPoints.length;
  for (const { bpm } of hrPoints) {
    let placed = false;
    for (let z = 0; z <= 5; z++) {
      const [lo, hi] = thresholds[z];
      if (bpm >= lo && bpm < hi) {
        durations[z] += perSample;
        placed = true;
        break;
      }
    }
    if (!placed && bpm >= thresholds[5][0]) durations[5] += perSample;
    if (!placed && bpm < thresholds[0][0]) durations[0] += perSample;
  }
  return durations;
}

export function calculateCalories({ zoneDurations, weightKg = 70 }) {
  let total = 0;
  for (const [z, secs] of Object.entries(zoneDurations)) {
    const met = ZONE_METS[z] ?? 0;
    total += met * weightKg * (secs / 3600);
  }
  return Math.round(total);
}

export function intensityPercent(sessionMaxBpm, ageBasedMhr) {
  if (!sessionMaxBpm || !ageBasedMhr) return 0;
  return Math.min(100, Math.round((sessionMaxBpm / ageBasedMhr) * 100));
}

export function formatDuration(totalSec) {
  const n = Number(totalSec);
  if (!Number.isFinite(n) || n < 0) {
    return "00:00:00";
  }
  const s = Math.max(0, Math.round(n));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

export function isDarkModeActive() {
  if (typeof document === "undefined") return false;
  // If explicitly light, never treat as dark mode
  const dataTheme = document.documentElement.getAttribute("data-theme");
  if (
    dataTheme === "light" ||
    document.body.classList.contains("light-mode") ||
    document.body.classList.contains("light")
  ) {
    return false;
  }
  return (
    dataTheme === "dark" ||
    document.body.classList.contains("dark") ||
    document.body.classList.contains("dark-mode") ||
    (!dataTheme &&
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-color-scheme: dark)")?.matches === true)
  );
}

